import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, SectionTitle } from "@/components/common/Page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RichTextEditor } from "@/components/legal/RichTextEditor";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, FileDown, Eye, GitBranch, ClipboardCheck, ShieldCheck,
  History, ScrollText, AlertTriangle, FileType2,
} from "lucide-react";
import { Badge, templateStatusColor, reviewStatusColor, openVersionPdf, downloadVersionDocx } from "@/pages/legal/legalConfig";

const inputCls = "border-[#2D3F62] bg-[#0B1120] text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-500";
const today = () => new Date().toISOString().slice(0, 10);

export default function TemplateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [verOpen, setVerOpen] = useState(false);
  const [revOpen, setRevOpen] = useState(false);
  const [appOpen, setAppOpen] = useState(false);
  const [ver, setVer] = useState({ version: "", change_notes: "", body_html: "", administrative_only: false });
  const [rev, setRev] = useState({ version: "", reviewer_name: "", reviewer_org: "", reviewer_email: "", reviewer_type: "external", due_date: "", instructions: "" });
  const [app, setApp] = useState({ version: "", approved_by: "", organisation: "", approval_date: today(), notes: "" });

  const { data: t } = useQuery({ queryKey: ["legal-template", id], queryFn: async () => (await api.get(`/legal/templates/${id}`)).data });

  const refresh = () => { qc.invalidateQueries({ queryKey: ["legal-template", id] }); qc.invalidateQueries({ queryKey: ["legal-templates"] }); };

  const newVersion = useMutation({
    mutationFn: async () => (await api.post(`/legal/templates/${id}/versions`, ver)).data,
    onSuccess: () => { refresh(); setVerOpen(false); toast.success("New version created"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const sendReview = useMutation({
    mutationFn: async () => (await api.post(`/legal/reviews`, { subject_type: "template", subject_id: id, ...rev })).data,
    onSuccess: () => { refresh(); setRevOpen(false); toast.success("Sent for legal review"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const recordApproval = useMutation({
    mutationFn: async () => (await api.post(`/legal/templates/${id}/record-approval`, app)).data,
    onSuccess: () => { refresh(); setAppOpen(false); toast.success("Legal approval recorded"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  if (!t) return null;
  const latest = t.versions?.[t.versions.length - 1];
  const openVer = () => { setVer({ version: "", change_notes: "", body_html: latest?.body_html || "", administrative_only: false }); setVerOpen(true); };
  const openReview = () => { setRev({ ...rev, version: t.current_version }); setRevOpen(true); };
  const openApproval = () => { setApp({ ...app, version: t.current_version }); setAppOpen(true); };

  return (
    <div data-testid="template-detail">
      <button onClick={() => navigate("/legal/templates")} className="mb-3 flex items-center gap-1.5 text-sm text-slate-400 hover:text-cyan-300" data-testid="back-to-templates">
        <ArrowLeft className="h-4 w-4" /> Templates
      </button>
      <PageHeader title={t.name} subtitle={`${t.category} · ${t.jurisdiction}`}>
        <Button variant="outline" onClick={() => openVersionPdf(id, t.current_version, "clean")} className="border-[#2D3F62] bg-transparent text-slate-200 hover:bg-[#17233B]" data-testid="preview-clean-pdf">
          <Eye className="mr-1.5 h-4 w-4" /> Preview PDF
        </Button>
        <Button variant="outline" onClick={() => downloadVersionDocx(id, t.current_version)} className="border-[#2D3F62] bg-transparent text-slate-200 hover:bg-[#17233B]" data-testid="download-docx">
          <FileType2 className="mr-1.5 h-4 w-4" /> DOCX
        </Button>
        <Button onClick={openVer} className="bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900" data-testid="new-version-btn">
          <GitBranch className="mr-1.5 h-4 w-4" /> New Version
        </Button>
      </PageHeader>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-4">
          <SectionTitle className="mb-2">Status</SectionTitle>
          <div className="flex flex-wrap gap-2">
            <Badge value={t.status} map={templateStatusColor} testId="detail-template-status" />
            <Badge value={t.legal_review_status} map={reviewStatusColor} testId="detail-review-status" />
          </div>
          <p className="mt-3 text-xs text-slate-500">Current version <span className="font-mono text-slate-300">{t.current_version}</span></p>
          {t.approved_version && <p className="mt-1 text-xs text-emerald-300">Approved {t.approved_version} by {t.approved_by} ({t.approved_org || "—"})</p>}
        </div>
        <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-4 md:col-span-2">
          <SectionTitle className="mb-2">Actions</SectionTitle>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={openReview} className="border-sky-500/30 bg-transparent text-sky-300 hover:bg-sky-500/10" data-testid="send-review-btn">
              <ClipboardCheck className="mr-1.5 h-4 w-4" /> Send for Legal Review
            </Button>
            <Button variant="outline" onClick={openApproval} className="border-emerald-500/30 bg-transparent text-emerald-300 hover:bg-emerald-500/10" data-testid="record-approval-btn">
              <ShieldCheck className="mr-1.5 h-4 w-4" /> Record Legal Approval
            </Button>
          </div>
          {t.status === "Legal Review Required" && (
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200" data-testid="review-required-warning">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> Draft legal template — Australian commercial/technology lawyer review required before production use.
            </div>
          )}
          {t.internal_notes && (
            <p className="mt-3 flex items-start gap-2 text-xs text-slate-500"><ScrollText className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {t.internal_notes}</p>
          )}
        </div>
      </div>

      <SectionTitle className="mb-3 mt-8">Version History (immutable)</SectionTitle>
      <div className="space-y-2" data-testid="version-history">
        {(t.versions || []).slice().reverse().map((v) => (
          <div key={v.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[#1E2D4A] bg-[#111A2E]/60 px-4 py-3" data-testid={`version-${v.version}`}>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm text-slate-100">{v.version}</span>
                <Badge value={v.legal_review_status} map={reviewStatusColor} />
              </div>
              <p className="text-xs text-slate-500">{v.change_notes} · {v.created_by_name} · {new Date(v.created_at).toLocaleDateString()}</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => openVersionPdf(id, v.version, "review")} className="border-[#2D3F62] bg-transparent text-amber-300 hover:bg-[#17233B]" data-testid={`review-pdf-${v.version}`}>
                <FileDown className="mr-1 h-3.5 w-3.5" /> Review copy
              </Button>
              <Button size="sm" variant="outline" onClick={() => openVersionPdf(id, v.version, "clean")} className="border-[#2D3F62] bg-transparent text-slate-200 hover:bg-[#17233B]" data-testid={`clean-pdf-${v.version}`}>
                <Eye className="mr-1 h-3.5 w-3.5" /> PDF
              </Button>
              <Button size="sm" variant="outline" onClick={() => downloadVersionDocx(id, v.version)} className="border-[#2D3F62] bg-transparent text-slate-200 hover:bg-[#17233B]" data-testid={`docx-${v.version}`}>
                <FileType2 className="mr-1 h-3.5 w-3.5" /> DOCX
              </Button>
            </div>
          </div>
        ))}
      </div>

      <SectionTitle className="mb-3 mt-8"><History className="mr-1 inline h-3.5 w-3.5" /> Review & Audit History</SectionTitle>
      <div className="space-y-1.5" data-testid="review-history">
        {(t.reviews || []).map((r) => (
          <div key={r.id} className="rounded-lg border border-[#1E2D4A] bg-[#111A2E]/50 px-4 py-2.5 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-slate-200">Review {r.version} — {r.reviewer_name} ({r.reviewer_org || r.reviewer_type})</span>
              <Badge value={r.status} map={reviewStatusColor} />
            </div>
            {r.documents?.length > 0 && <p className="mt-1 text-xs text-slate-500">{r.documents.length} attachment(s) received</p>}
          </div>
        ))}
        {(t.history || []).slice(0, 12).map((h, i) => (
          <div key={i} className="px-4 py-1 text-xs text-slate-500">
            {h.meta?.event || h.action} · {h.user_email || "system"} · {new Date(h.timestamp).toLocaleString()}
          </div>
        ))}
      </div>

      {/* New version dialog */}
      <Dialog open={verOpen} onOpenChange={setVerOpen}>
        <DialogContent className="border-[#1E2D4A] bg-[#111A2E] text-slate-100 sm:max-w-2xl" data-testid="new-version-dialog">
          <DialogHeader><DialogTitle>Create New Version</DialogTitle></DialogHeader>
          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-400">New version (e.g. v1.1)</Label>
                <Input value={ver.version} onChange={(e) => setVer({ ...ver, version: e.target.value })} placeholder="v1.1" className={`mt-1 ${inputCls}`} data-testid="version-number-input" />
              </div>
              <div className="flex items-end gap-2 pb-1">
                <Switch checked={ver.administrative_only} onCheckedChange={(v) => setVer({ ...ver, administrative_only: v })} data-testid="admin-only-switch" />
                <span className="text-xs text-slate-400">Administrative only (does not change wording)</span>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Change notes (required)</Label>
              <Input value={ver.change_notes} onChange={(e) => setVer({ ...ver, change_notes: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="change-notes-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Body</Label>
              <div className="mt-1"><RichTextEditor value={ver.body_html} onChange={(html) => setVer({ ...ver, body_html: html })} testId="version-body" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => newVersion.mutate()} disabled={newVersion.isPending || !ver.version || !ver.change_notes} className="bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900" data-testid="version-create-confirm">Create Version</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send for review dialog */}
      <Dialog open={revOpen} onOpenChange={setRevOpen}>
        <DialogContent className="border-[#1E2D4A] bg-[#111A2E] text-slate-100 sm:max-w-lg" data-testid="send-review-dialog">
          <DialogHeader><DialogTitle>Send for Legal Review</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <p className="text-xs text-slate-500">A review record is tied to version <span className="font-mono text-slate-300">{rev.version}</span>. The underlying version is never modified.</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-400">Reviewer name</Label><Input value={rev.reviewer_name} onChange={(e) => setRev({ ...rev, reviewer_name: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="reviewer-name-input" /></div>
              <div><Label className="text-xs text-slate-400">Organisation</Label><Input value={rev.reviewer_org} onChange={(e) => setRev({ ...rev, reviewer_org: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="reviewer-org-input" /></div>
              <div><Label className="text-xs text-slate-400">Email / contact</Label><Input value={rev.reviewer_email} onChange={(e) => setRev({ ...rev, reviewer_email: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="reviewer-email-input" /></div>
              <div>
                <Label className="text-xs text-slate-400">Reviewer type</Label>
                <Select value={rev.reviewer_type} onValueChange={(v) => setRev({ ...rev, reviewer_type: v })}>
                  <SelectTrigger className={`mt-1 ${inputCls}`} data-testid="reviewer-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100">
                    <SelectItem value="internal">Internal</SelectItem>
                    <SelectItem value="external">External</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs text-slate-400">Review due date</Label><Input type="date" value={rev.due_date} onChange={(e) => setRev({ ...rev, due_date: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="review-due-input" /></div>
            </div>
            <div><Label className="text-xs text-slate-400">Instructions</Label><Textarea rows={3} value={rev.instructions} onChange={(e) => setRev({ ...rev, instructions: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="review-instructions-input" /></div>
          </div>
          <DialogFooter>
            <Button onClick={() => sendReview.mutate()} disabled={sendReview.isPending || !rev.reviewer_name} className="bg-gradient-to-r from-sky-500 to-cyan-600 font-semibold text-slate-900" data-testid="send-review-confirm">Send for Review</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record approval dialog */}
      <Dialog open={appOpen} onOpenChange={setAppOpen}>
        <DialogContent className="border-[#1E2D4A] bg-[#111A2E] text-slate-100 sm:max-w-lg" data-testid="record-approval-dialog">
          <DialogHeader><DialogTitle>Record Legal Approval</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <p className="text-xs text-slate-500">Explicitly records approval for version <span className="font-mono text-slate-300">{app.version}</span>. Uploading a review file alone does not approve a document.</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-400">Approved by</Label><Input value={app.approved_by} onChange={(e) => setApp({ ...app, approved_by: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="approved-by-input" /></div>
              <div><Label className="text-xs text-slate-400">Organisation</Label><Input value={app.organisation} onChange={(e) => setApp({ ...app, organisation: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="approval-org-input" /></div>
              <div><Label className="text-xs text-slate-400">Approval date</Label><Input type="date" value={app.approval_date} onChange={(e) => setApp({ ...app, approval_date: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="approval-date-input" /></div>
            </div>
            <div><Label className="text-xs text-slate-400">Notes</Label><Textarea rows={2} value={app.notes} onChange={(e) => setApp({ ...app, notes: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="approval-notes-input" /></div>
          </div>
          <DialogFooter>
            <Button onClick={() => recordApproval.mutate()} disabled={recordApproval.isPending || !app.approved_by} className="bg-gradient-to-r from-emerald-500 to-cyan-500 font-semibold text-slate-900" data-testid="approval-confirm">Record Approval</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
